"use client";

import Link from "next/link";
import React, { useCallback, useMemo } from "react";
import { ImageWrapper, Text } from "ui-components";

import { Slider } from "@/components/slider/Slider";

const awardsAchievements = [
  {
    logo: "https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/v1751530968/about_us/2026_finalist_bj7fb5.png",
    title:
      "myGwork celebrates double nomination at the 2026 IHR Supplier Awards!",
    link: "https://mygwork.com/news/mygwork-celebrates-double-nomination-at-the-2026-ihr-supplier-awards",
  },
  {
    logo: "https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/v1751530968/about_us/Awards_2025_kedozm.png",
    title:
      "myGwork scoops Specialist Recruitment Agency of the Year at the British Recruitment Awards 2025",
    link: "https://mygwork.com/news/mygwork-scoops-specialist-recruitment-agency-of-the-year-at-the-british-recruitment-awards-2025",
  },
  {
    logo: "https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/v1751530968/about_us/rainbow_tk72ga.png",
    title: "myGwork’s WorkPride scoops the coveted 2025 Rainbow Honours Award",
    link: "https://mygwork.com/news/mygworks-workpride-scoops-coveted-rainbow-honours-award-at-star-studded-ceremony-with-celebrities-and-corporate-allies",
  },
  {
    logo: "https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/v1744197694/about_us/forbes_d4vzbb.png",
    title: "myGwork accepted into Forbes Business Council",
    link: "",
  },
  {
    logo: "https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/about_us/awards_achievements/innovate_finance.png",
    title: "myGwork’s Co-founders honored in Pride in FinTech Powerlist 2024",
    link: "https://mygwork.com/news/mygworks-co-founders-honored-in-pride-in-fintech-powerlist-2024-for-championing-lgbtq-inclusion-in-financial-services",
  },
  {
    logo: "https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/about_us/awards_achievements/national_diversity_awards.png",
    title:
      "myGwork’s co-founders shortlisted in 2024's National Diversity Awards",
    link: "https://mygwork.com/news/mygworks-co-founders-shortlisted-for-the-national-diversity-awards-2024-alongside-community-heroes-celebrities",
  },
  {
    logo: "https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/about_us/awards_achievements/inclusive_awards.png",
    title: "myGwork Scoops 2023 Inclusive Award For D&I Tech Initiative",
    link: "https://mygwork.com/news/mygwork-scoops-2023-inclusive-award-for-d-i-tech-initiative",
  },
  {
    logo: "https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/about_us/awards_achievements/diversity_power.png",
    title:
      "myGwork’s Founders Honored in UK’s Inaugural Diversity Power List 2022/23",
    link: "https://www.mygwork.com/news/mygworks-founders-honored-in-uks-inaugural-diversity-power-list-2022-23",
  },
  {
    logo: "https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/about_us/awards_achievements/london_rainbow_honours.png",
    title: "myGwork Scoops 2022 Bank of London Rainbow Honours Award",
    link: "https://www.mygwork.com/news/mygwork-scoops-2022-bank-of-london-rainbow-honours-award",
  },
  {
    logo: "https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/about_us/awards_achievements/digital_leaders_impact_awards.png",
    title: "myGwork Shortlisted For The 2022 Digital Leaders Impact Awards",
    link: "https://www.mygwork.com/news/mygwork-shortlisted-for-the-2022-digital-leaders-impact-awards",
  },
  {
    logo: "https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/about_us/awards_achievements/tlc_lions.png",
    title:
      "Finalist in 2023's Being Human Awards in Best Use of Technology category",
    link: "https://mygwork.com/news/being-human-awards-tlc-lions-reveal-2023s-winners-finalists",
  },
];

interface IAwardsAchievementsProps {
  assetUrl?: string;
}

export default function AwardsAchievements({
  assetUrl: _assetUrl,
}: Readonly<IAwardsAchievementsProps>): JSX.Element {
  const renderDots = useCallback(
    (dots: React.ReactNode) => (
      <ul className="flex flex-nowrap items-center justify-center gap-2 whitespace-nowrap">
        {dots}
      </ul>
    ),
    []
  );

  const settings = {
    dots: false,
    infinite: true,
    arrows: false,
    speed: 500,
    slidesToShow: 3,
    autoplay: true,
    autoplaySpeed: 20000,
    pauseOnHover: true,
    appendDots: renderDots,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          slidesPerRow: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          dots: true,
          arrows: true,
          autoplay: false,
          pauseOnHover: false,
          slidesToShow: 1,
          slidesToScroll: 1,
          rows: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          dots: true,
          arrows: true,
          autoplay: false,
          pauseOnHover: false,
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const renderItems = useMemo(
    () =>
      awardsAchievements?.map((award, index) => (
        <div
          key={`slide-${index.toString()}`}
          className="flex flex-col sm:flex-row items-center text-center sm:text-left mx-auto gap-y-4 gap-x-6 pb-4 mt-2"
        >
          <div className="relative w-auto h-[252px] md:w-[160px] md:h-[145px] rounded">
            <ImageWrapper
              src={award.logo}
              alt={award.title}
              objectFit="contain"
              fill
              unoptimized
            />
          </div>
          <div className="flex flex-col w-[252px] !md:w-full mx-auto md:float-left mt-5">
            <Text className="!text-lg !font-bold">{award.title}</Text>
            {award.link && (
              <Link
                href={award.link}
                target="_blank"
                className="text-sm mt-2 text-left"
              >
                Read More
              </Link>
            )}
          </div>
        </div>
      )),
    []
  );

  return (
    <div className="w-[323px] md:w-[900px] mt-10 mx-auto">
      <Slider settings={settings}>{renderItems}</Slider>
    </div>
  );
}
